import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatNumber, getDisplayLabel } from "src/helpers/api/sharedFunction";
import { InsiderAcquisition } from "src/helpers";

interface CategorizationAmtProps {
  insiderAcquisition: InsiderAcquisition[];
  selectedModes: string;
  onModeSelect: (modes: string) => void;
}

const CategorizationAmtCard = ({
  insiderAcquisition,
  selectedModes,
  onModeSelect,
}: CategorizationAmtProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    if (!selectedModes || !scrollRef.current) return;

    const scrollElement = scrollRef.current;
    const selectedCard = scrollElement.querySelector(
      `[data-mode='${selectedModes}']`,
    ) as HTMLElement | null;

    if (selectedCard) {
      selectedCard.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    }
  }, [selectedModes]);

  useEffect(() => {
    updateScrollState();
  }, [insiderAcquisition]);

  const handleSelect = (mode: string) => {
    const updated = selectedModes === mode ? "" : mode;
    onModeSelect(updated);
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  return (
    <div className="relative flex items-center border border-[#d0d4e4] rounded-md dark:border-0 bg-white dark:bg-[#121419] w-full shadow-[5px_1px_5px_rgba(0,0,0,0.09)] px-4 py-2 min-h-[80px]">
      {insiderAcquisition.length === 0 ? (
        <div className="flex items-center justify-center w-full h-full">
          <p className="text-gray-600 dark:text-gray-300 text-center">
            No acquisition modes have been found for the selected date range.
          </p>
        </div>
      ) : (
        <>
          <button
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            className={`absolute -left-2 top-1/2 -translate-y-1/2 bg-white dark:bg-[#121419] p-2 rounded-full shadow z-20 text-lg transition-colors focus:outline-none flex items-center justify-center border border-gray-200 dark:border-gray-700 ${
              canScrollLeft
                ? "hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                : "opacity-50 cursor-not-allowed"
            }`}
            aria-label="Scroll left"
            style={{ width: "32px", height: "32px" }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            className="flex-1 flex overflow-x-auto gap-4 hide-scrollbar"
          >
            {insiderAcquisition.map((item, i) => {
              const isSelected = selectedModes.includes(item.acquisition_mode);
              return (
                <div
                  key={i}
                  data-mode={item.acquisition_mode}
                  onClick={() => handleSelect(item.acquisition_mode)}
                  className={`min-w-[200px] w-[200px] flex-shrink-0 rounded-lg px-3 py-2 border flex flex-col justify-center cursor-pointer transition-all
                    ${
                      isSelected
                        ? "bg-green-100/90 border-grey-500 dark:bg-green-900/40 border-gray-200 dark:border-gray-700/50"
                        : "bg-gray-100/30 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700/50 backdrop-blur-md shadow-inner shadow-gray-300/20 dark:shadow-gray-900/20"
                    }`}
                >
                  <p className="text-sm font-medium truncate dark:text-white">
                    {getDisplayLabel(item.acquisition_mode)}
                  </p>
                  <p className="text-xl font-medium text-black dark:text-white mt-1">
                    {formatNumber(item.total)}
                  </p>
                </div>
              );
            })}
          </div>

          <button
            onClick={scrollRight}
            disabled={!canScrollRight}
            className={`absolute -right-2 top-1/2 -translate-y-1/2 bg-white dark:bg-[#121419] p-2 rounded-full shadow z-20 text-lg transition-colors focus:outline-none flex items-center justify-center border border-gray-200 dark:border-gray-700 ${
              canScrollRight
                ? "hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                : "opacity-50 cursor-not-allowed"
            }`}
            aria-label="Scroll right"
            style={{ width: "32px", height: "32px" }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default React.memo(CategorizationAmtCard);
